import { Injectable } from '@angular/core';

/**
 * #7555 Motherhood multi-UOM conversion.
 * Qty (PO UOM)   = invoice_uom_qty * uom_conversion_rate
 * Price (PO UOM) = invoice_uom_price / uom_conversion_rate
 * Mirrors Uom_conversion_helper_modal on the API side.
 */
@Injectable()
export class UomConversionService {
  readonly FIELD_UOM_ID: string = 'invoice_uom_id';
  readonly FIELD_UOM_QTY: string = 'invoice_uom_qty';
  readonly FIELD_RATE: string = 'uom_conversion_rate';
  readonly FIELD_UOM_PRICE: string = 'invoice_uom_price';

  /**
   * Company meta key (Company Setup → General):
   * enable_multi_uom_conversion_in_grn_and_invoice
   *
   * Explicit '0' → page toggle defaults OFF.
   * Explicit '1' or missing key → page toggle defaults ON (backward compatible).
   * Column visibility is controlled by the page toggle, not this key alone.
   * Limited-meta pages must include this key in variable_list.
   */
  readonly MULTI_UOM_SETUP_KEY: string = 'enable_multi_uom_conversion_in_grn_and_invoice';

  /**
   * Default for the page-level "Invoice UOM conversion" toggle from company setup.
   * Setup '1' / missing → true (show). Setup '0' → false (hide).
   */
  defaultPageToggleFromSetup(companySetup?: any): boolean {
    return this.resolveMultiUomEnabled(companySetup) !== false;
  }

  /**
   * SRS #4: when company setup parent toggle is OFF, page-level toggle must be
   * locked off (disabled) so the user cannot enable UOM columns.
   */
  isPageToggleLockedOff(companySetup?: any): boolean {
    return this.resolveMultiUomEnabled(companySetup) === false;
  }

  /**
   * Whether the 4 Invoice UOM columns should render.
   * Parent setup OFF always hides columns, even if page toggle is somehow true.
   * Prefer explicit page toggle; fall back to setup default when toggle not yet set.
   */
  shouldShowInvoiceUomColumns(companySetup?: any, pageToggle?: boolean): boolean {
    if (this.isPageToggleLockedOff(companySetup)) {
      return false;
    }
    if (pageToggle === true || pageToggle === false) {
      return pageToggle;
    }
    return this.defaultPageToggleFromSetup(companySetup);
  }

  /**
   * SRS #6: when multi-UOM conversion UI is active, PO-UOM qty/price targets
   * (Current Delivery / Received Qty and Current Invoice Price) are frozen
   * and must only be auto-calculated from the 4 Invoice UOM columns.
   */
  areConvertedFieldsFrozen(companySetup?: any, pageToggle?: boolean): boolean {
    return this.shouldShowInvoiceUomColumns(companySetup, pageToggle) === true;
  }

  /**
   * @deprecated Prefer shouldShowInvoiceUomColumns(companySetup, pageToggle).
   * Kept for screens not yet wired to page toggle — same as setup default.
   */
  isMultiUomConversionEnabled(companySetup?: any): boolean {
    return this.defaultPageToggleFromSetup(companySetup);
  }

  /** true | false | null (null = setup not ready / key unknown — treat as show). */
  resolveMultiUomEnabled(companySetup?: any): any {
    if (!companySetup || typeof companySetup !== 'object') {
      return null;
    }
    if (Object.keys(companySetup).length === 0) {
      return null;
    }
    if (companySetup[this.MULTI_UOM_SETUP_KEY] !== undefined
      && companySetup[this.MULTI_UOM_SETUP_KEY] !== null
      && companySetup[this.MULTI_UOM_SETUP_KEY] !== '') {
      let raw: any = companySetup[this.MULTI_UOM_SETUP_KEY];
      if (raw === '1' || raw === 1 || raw === true || raw === 'true') {
        return true;
      }
      if (raw === '0' || raw === 0 || raw === false || raw === 'false') {
        return false;
      }
    }
    return null;
  }

  /** Column keys shown when multi-UOM conversion is enabled (summary / approval tables). */
  readonly DISPLAY_COLUMN_VALUES: string[] = [
    'invoice_uom_name', 'invoice_uom_qty', 'uom_conversion_rate', 'invoice_uom_price'
  ];

  /** Toggle is_view on the 4 Invoice UOM columns from page toggle (or setup default). */
  applyColumnVisibility(columnList: any, companySetup?: any, pageToggle?: boolean): void {
    if (!columnList || !columnList.length) {
      return;
    }
    let show: boolean = this.shouldShowInvoiceUomColumns(companySetup, pageToggle);
    for (let col of columnList) {
      if (col && this.DISPLAY_COLUMN_VALUES.indexOf(col.value) !== -1) {
        col.is_view = show;
      }
    }
  }

  /** Normalize display fields for read-only summary / accordion rows. */
  enrichDisplayRow(row: any): void {
    if (!row) {
      return;
    }
    let name: any = (row.invoice_uom_name !== undefined && row.invoice_uom_name !== null) ? row.invoice_uom_name : '';
    // Never show a bare numeric id as the UOM label.
    if (name !== '' && row.invoice_uom_id && String(name) === String(row.invoice_uom_id)) {
      name = '';
    }
    if (name === '' && row.invoice_uom_id) {
      name = row.unit_type_name_invoice || row.invoice_uom_unit_type || row.invoice_uom_unit_name || '';
    }
    row.invoice_uom_name = name;
    row.invoice_uom_qty = (row.invoice_uom_qty !== undefined && row.invoice_uom_qty !== null) ? row.invoice_uom_qty : '';
    row.uom_conversion_rate = (row.uom_conversion_rate !== undefined && row.uom_conversion_rate !== null) ? row.uom_conversion_rate : '';
    row.invoice_uom_price = (row.invoice_uom_price !== undefined && row.invoice_uom_price !== null) ? row.invoice_uom_price : '';
  }

  /**
   * Fill blank invoice_uom_name from unit_type_master when id is present.
   * Used on summary/approval tables where some GET APIs omit the join alias.
   * Optional onDone runs after names are resolved (use to refresh bound arrays).
   */
  enrichDisplayRowsWithNames(items: any, webService: any, companyId: any, userId: any, onDone?: any): void {
    if (!items || !items.length) {
      return;
    }
    for (let row of items) {
      this.enrichDisplayRow(row);
    }
    let missing: any = [];
    for (let row of items) {
      if (row && row.invoice_uom_id && (row.invoice_uom_name === undefined || row.invoice_uom_name === null || row.invoice_uom_name === '')) {
        missing.push(row);
      }
    }
    if (!missing.length || !webService) {
      if (onDone) {
        onDone();
      }
      return;
    }
    this.loadInvoiceUomList(webService, companyId, userId, (list: any) => {
      if (!list || !list.length) {
        if (onDone) {
          onDone();
        }
        return;
      }
      let map: any = {};
      for (let u of list) {
        if (!u) {
          continue;
        }
        map[u.unit_id] = u.unit_type_name;
        map['' + u.unit_id] = u.unit_type_name;
      }
      for (let row of missing) {
        let resolved: any = map[row.invoice_uom_id] || map['' + row.invoice_uom_id] || '';
        if (resolved) {
          row.invoice_uom_name = resolved;
        }
      }
      if (onDone) {
        onDone();
      }
    });
  }

  normalizeRate(rate: any): number {
    let parsed: any = parseFloat(rate);
    if (isNaN(parsed) || parsed <= 0) {
      return 1;
    }
    return parsed;
  }

  calcPoQty(invoiceUomQty: any, rate: any): any {
    if (invoiceUomQty === '' || invoiceUomQty == null) {
      return '';
    }
    let qty: any = parseFloat(invoiceUomQty);
    if (isNaN(qty)) {
      return '';
    }
    return qty * this.normalizeRate(rate);
  }

  calcPoPrice(invoiceUomPrice: any, rate: any): any {
    if (invoiceUomPrice === '' || invoiceUomPrice == null) {
      return '';
    }
    let price: any = parseFloat(invoiceUomPrice);
    if (isNaN(price)) {
      return '';
    }
    return price / this.normalizeRate(rate);
  }

  /** Billable qty from PO qty: invoice_uom_qty = po_qty / rate */
  calcInvoiceUomQtyFromPoQty(poQty: any, rate: any): any {
    if (poQty === '' || poQty == null) {
      return '';
    }
    let qty: any = parseFloat(poQty);
    if (isNaN(qty)) {
      return '';
    }
    return qty / this.normalizeRate(rate);
  }

  /** Billable unit price from PO unit price: invoice_uom_price = po_price * rate */
  calcInvoiceUomPriceFromPoPrice(poPrice: any, rate: any): any {
    if (poPrice === '' || poPrice == null) {
      return '';
    }
    let price: any = parseFloat(poPrice);
    if (isNaN(price)) {
      return '';
    }
    return price * this.normalizeRate(rate);
  }

  /**
   * After master rate lookup: fill invoice_uom_qty / invoice_uom_price from PO remaining qty & unit price.
   * Does not overwrite qty/price if caller passes overwrite=false and values already set.
   */
  autoFillFromMaster(row: any, poQtyField: any, poPriceField: any, digits?: any, overwrite?: boolean): void {
    if (!row) {
      return;
    }
    let rate: number = this.normalizeRate(row[this.FIELD_RATE]);
    let qtyDigits: number = this.resolveDigits(digits, 4);
    let priceDigits: number = Math.max(qtyDigits, 4);
    let force: boolean = overwrite !== false;

    let poQty: any = this.isFilled(row[poQtyField]) ? row[poQtyField] : row.balance_to_be_delivered_qty;
    if (!this.isFilled(poQty)) {
      poQty = row.quantity;
    }
    if (force || !this.isFilled(row[this.FIELD_UOM_QTY])) {
      let billableQty: any = this.calcInvoiceUomQtyFromPoQty(poQty, rate);
      if (billableQty !== '') {
        row[this.FIELD_UOM_QTY] = this.roundTo(billableQty, qtyDigits);
      }
    }

    let poPrice: any = this.isFilled(row[poPriceField]) ? row[poPriceField] : '';
    if (!this.isFilled(poPrice)) {
      poPrice = this.isFilled(row.unit_price) ? row.unit_price : row.product_unit_price;
    }
    if (force || !this.isFilled(row[this.FIELD_UOM_PRICE])) {
      let billablePrice: any = this.calcInvoiceUomPriceFromPoPrice(poPrice, rate);
      if (billablePrice !== '') {
        row[this.FIELD_UOM_PRICE] = this.roundTo(billablePrice, priceDigits);
      }
    }
  }

  /** True when the row carries any of the 4 conversion inputs. */
  hasConversionInput(row: any): boolean {
    if (!row) {
      return false;
    }
    return this.isFilled(row[this.FIELD_UOM_ID]) || this.isFilled(row[this.FIELD_UOM_QTY]) ||
      this.isFilled(row[this.FIELD_RATE]) || this.isFilled(row[this.FIELD_UOM_PRICE]);
  }

  /** Seed the 4 fields on a line item, keeping any value already sent by the API. */
  initRowFields(row: any): void {
    if (!row) {
      return;
    }
    row[this.FIELD_UOM_ID] = this.isFilled(row[this.FIELD_UOM_ID]) ? row[this.FIELD_UOM_ID] : '';
    row[this.FIELD_UOM_QTY] = this.isFilled(row[this.FIELD_UOM_QTY]) ? row[this.FIELD_UOM_QTY] : '';
    row[this.FIELD_RATE] = this.isFilled(row[this.FIELD_RATE]) ? row[this.FIELD_RATE] : '';
    row[this.FIELD_UOM_PRICE] = this.isFilled(row[this.FIELD_UOM_PRICE]) ? row[this.FIELD_UOM_PRICE] : '';
  }

  /**
   * Recalculate the legacy qty/price fields from the conversion inputs.
   * Leaves the legacy fields untouched when none of the 4 fields is filled,
   * so screens without multi-UOM keep behaving exactly as before.
   */
  applyLiveConversion(row: any, qtyTargetField: any, priceTargetField: any, digits?: any): void {
    if (!row || !this.hasConversionInput(row)) {
      return;
    }
    let rate: number = this.normalizeRate(row[this.FIELD_RATE]);
    let qtyDigits: number = this.resolveDigits(digits, 4);
    let priceDigits: number = Math.max(qtyDigits, 4);

    let qtyFields: any = this.toFieldList(qtyTargetField);
    if (qtyFields.length > 0 && this.isFilled(row[this.FIELD_UOM_QTY])) {
      let poQty: any = this.calcPoQty(row[this.FIELD_UOM_QTY], rate);
      if (poQty !== '') {
        poQty = this.roundTo(poQty, qtyDigits);
        for (let f of qtyFields) {
          row[f] = poQty;
        }
      }
    }

    let priceFields: any = this.toFieldList(priceTargetField);
    if (priceFields.length > 0 && this.isFilled(row[this.FIELD_UOM_PRICE])) {
      let poPrice: any = this.calcPoPrice(row[this.FIELD_UOM_PRICE], rate);
      if (poPrice !== '') {
        poPrice = this.roundTo(poPrice, priceDigits);
        for (let f of priceFields) {
          row[f] = poPrice;
        }
      }
    }
  }

  /** The 4 keys to merge into each line item of a save payload. */
  buildPayloadFields(row: any): any {
    if (!row) {
      return {};
    }
    return {
      invoice_uom_id: this.isFilled(row[this.FIELD_UOM_ID]) ? row[this.FIELD_UOM_ID] : '',
      invoice_uom_qty: this.isFilled(row[this.FIELD_UOM_QTY]) ? row[this.FIELD_UOM_QTY] : '',
      uom_conversion_rate: this.isFilled(row[this.FIELD_RATE]) ? row[this.FIELD_RATE] : '',
      invoice_uom_price: this.isFilled(row[this.FIELD_UOM_PRICE]) ? row[this.FIELD_UOM_PRICE] : ''
    };
  }

  /** Conversion factor from standard_uom_conversion_master; falls back to 1. */
  lookupRate(webService: any, companyId: any, invoiceUomId: any, baseUomId?: any): any {
    let requestObj: any = {
      company_id: companyId == undefined ? '0' : companyId,
      invoice_uom_id: invoiceUomId ? invoiceUomId : '',
      billable_uom_id: invoiceUomId ? invoiceUomId : ''
    };
    if (baseUomId) {
      requestObj.base_uom_id = baseUomId;
    }
    return webService.commonPostMethod('/Standard_uom_conversion_master/ajax_lookup_rate', requestObj);
  }

  /** Rate from a lookup response, defaulting to 1 when the master has no row. */
  readRateFromResponse(data: any): number {
    if (!data || !data.status) {
      return 1;
    }
    let rate: any = data.uom_conversion_rate != null ? data.uom_conversion_rate : data.conversion_factor;
    return this.normalizeRate(rate);
  }

  /** True when ajax_lookup_rate found a master row (or same billable/base UOM). */
  isMasterFoundFromResponse(data: any): boolean {
    if (!data || !data.status) {
      return false;
    }
    if (data.found === true || data.master_found === true || data.found === 1 || data.master_found === 1
      || data.found === '1' || data.master_found === '1') {
      return true;
    }
    if (data.found === false || data.master_found === false || data.found === 0 || data.master_found === 0
      || data.found === '0' || data.master_found === '0') {
      return false;
    }
    return false;
  }

  /**
   * Apply ajax_lookup_rate onto a line row.
   * Unmapped Invoice UOM → mandated default conversion rate 1 (SRS / #7555).
   * Returns whether a master mapping was found.
   */
  applyLookupRateToRow(row: any, data: any, overwrite?: boolean): boolean {
    if (!row) {
      return false;
    }
    let force: boolean = overwrite !== false;
    let masterFound: boolean = this.isMasterFoundFromResponse(data);
    this.setMasterMissingFlag(row, !masterFound);
    if (!masterFound) {
      if (force || row[this.FIELD_RATE] === '' || row[this.FIELD_RATE] == null) {
        row[this.FIELD_RATE] = 1;
      }
      return false;
    }
    if (force || row[this.FIELD_RATE] === '' || row[this.FIELD_RATE] == null) {
      row[this.FIELD_RATE] = this.readRateFromResponse(data);
    }
    return true;
  }

  readonly MISSING_MASTER_MSG: string =
    'No conversion master found. Default conversion rate 1 applied.';

  /** Clear / set the under-dropdown hint when master is missing. */
  setMasterMissingFlag(row: any, missing: boolean): void {
    if (!row) {
      return;
    }
    row.uom_conversion_master_missing = !!missing;
    row.uom_conversion_master_msg = missing ? this.MISSING_MASTER_MSG : '';
  }

  /**
   * Invoice UOM dropdown for Start Delivery / IRN / GRN / Invoice.
   * Merges Unit Type Master + billable UOMs from Standard UOM Conversion Master
   * so conversion rows always appear even if unit-master approval is missing.
   * Shape: {unit_id, unit_type_name}
   */
  loadInvoiceUomList(webService: any, companyId: any, userId: any, callback: any): void {
    let requestObj: any = {
      company_id: companyId == undefined ? '0' : companyId,
      user_id: userId
    };
    let unitList: any = [];
    let conversionList: any = [];
    let pending: number = 2;
    let finish: any = () => {
      pending--;
      if (pending > 0) {
        return;
      }
      let list: any = unitList.concat(conversionList);
      if (webService && webService.removeDuplicates) {
        list = webService.removeDuplicates(list, 'unit_id');
      }
      callback(list);
    };

    webService.commonPostMethod('/Unit_type_master/unit_type_dropdown', requestObj).
      subscribe(
        data => {
          if (data && data.status && data.response) {
            for (let element of data.response) {
              unitList.push({ 'unit_id': String(element.id), 'unit_type_name': element.unit_type });
            }
          }
          finish();
        },
        error => {
          finish();
        }
      );

    let conversionReq: any = {
      company_id: companyId == undefined ? '0' : companyId,
      user_id: userId,
      page_no: 0,
      record_per_page: 5000
    };
    webService.commonPostMethod('/Standard_uom_conversion_master/ajax_list', conversionReq).
      subscribe(
        data => {
          if (data && data.status && data.response) {
            for (let row of data.response) {
              if (!row || !row.billable_uom_id) {
                continue;
              }
              conversionList.push({
                'unit_id': String(row.billable_uom_id),
                'unit_type_name': row.billable_uom_name || row.billable_uom_id
              });
            }
          }
          finish();
        },
        error => {
          finish();
        }
      );
  }

  /**
   * Match OCR / free-text UOM name (e.g. STRIP, 10ML V) to invoiceUomList unit_id.
   * Prefer exact name, then normalized, then prefix (handles abbreviated OCR packs).
   */
  matchUomIdByName(uomName: any, invoiceUomList: any): any {
    if (!uomName || !invoiceUomList || !invoiceUomList.length) {
      return '';
    }
    let raw: string = String(uomName).trim();
    if (!raw) {
      return '';
    }
    let lower: string = raw.toLowerCase();
    let normalized: string = this.normalizeUomName(raw);

    for (let u of invoiceUomList) {
      if (!u || u.unit_id === undefined || u.unit_id === null || u.unit_id === '') {
        continue;
      }
      let name: string = u.unit_type_name != null ? String(u.unit_type_name).trim() : '';
      if (!name) {
        continue;
      }
      if (name.toLowerCase() === lower) {
        return u.unit_id;
      }
    }

    for (let u of invoiceUomList) {
      if (!u || u.unit_id === undefined || u.unit_id === null || u.unit_id === '') {
        continue;
      }
      let name: string = u.unit_type_name != null ? String(u.unit_type_name).trim() : '';
      if (!name) {
        continue;
      }
      if (this.normalizeUomName(name) === normalized) {
        return u.unit_id;
      }
    }

    let bestId: any = '';
    let bestLen: number = 0;
    let bestMasterLen: number = 0;
    for (let u of invoiceUomList) {
      if (!u || u.unit_id === undefined || u.unit_id === null || u.unit_id === '') {
        continue;
      }
      let name: string = u.unit_type_name != null ? String(u.unit_type_name).trim() : '';
      if (!name) {
        continue;
      }
      let nName: string = this.normalizeUomName(name);
      if (!nName || !normalized) {
        continue;
      }
      if (nName.indexOf(normalized) === 0 || normalized.indexOf(nName) === 0) {
        let score: number = Math.min(nName.length, normalized.length);
        // Prefer longer master name when OCR is a prefix (10ML → 10ML VIAL over 1ML)
        if (score > bestLen || (score === bestLen && nName.length > bestMasterLen)) {
          bestLen = score;
          bestMasterLen = nName.length;
          bestId = String(u.unit_id);
        }
      }
    }
    return bestId;
  }

  /** Resolve display name for a unit_id from invoiceUomList. */
  resolveUomNameById(unitId: any, invoiceUomList: any): string {
    if (!unitId || !invoiceUomList || !invoiceUomList.length) {
      return '';
    }
    for (let u of invoiceUomList) {
      if (u && (u.unit_id == unitId || String(u.unit_id) === String(unitId))) {
        return u.unit_type_name ? String(u.unit_type_name) : '';
      }
    }
    return '';
  }

  private normalizeUomName(value: any): string {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private isFilled(value: any): boolean {
    return value !== '' && value !== null && value !== undefined;
  }

  private toFieldList(field: any): any[] {
    if (!field) {
      return [];
    }
    return Array.isArray(field) ? field : [field];
  }

  private resolveDigits(digits: any, fallback: number): number {
    let parsed: any = parseInt(digits, 10);
    if (isNaN(parsed) || parsed < 0) {
      return fallback;
    }
    return parsed;
  }

  private roundTo(value: any, digits: number): any {
    let parsed: any = parseFloat(value);
    if (isNaN(parsed)) {
      return '';
    }
    return parseFloat(parsed.toFixed(digits));
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src4281393277/src/src/app/shared/services/uom-conversion.service.ts